import { useEffect, useState, useCallback } from "react";
import { API_BASE } from "../lib/api";

const ADMIN_KEY = import.meta.env.VITE_ADMIN_KEY;

// ── Stub-mode gates (flip to false when corresponding backend lands) ──────────
const STUB_MODE_VIEW_1 = true; // No backend yet — awaiting /admin/speaker-audit/compounding
const STUB_MODE_VIEW_2 = false; // feat/vera-feedback-loop merged at 5a6c260
const STUB_MODE_VIEW_3 = false; // feat/vera-feedback-loop merged at 5a6c260
const STUB_MODE_VIEW_4 = false; // /admin/feedback-loop/growth live

// ── View 1: Compounding tracker ───────────────────────────────────────────────

interface CompoundingBucket {
  period_start: string;
  flag_rate: number;
  autofix_rate: number;
  total_audits: number;
}
interface CompoundingResponse {
  buckets: CompoundingBucket[];
  days: number;
}
const FIXTURE_VIEW_1: CompoundingResponse = {
  days: 90,
  buckets: [
    { period_start: "2026-04-01", flag_rate: 0.23, autofix_rate: 0.61, total_audits: 312 },
    { period_start: "2026-05-01", flag_rate: 0.19, autofix_rate: 0.67, total_audits: 287 },
    { period_start: "2026-06-01", flag_rate: 0.14, autofix_rate: 0.72, total_audits: 201 },
  ],
};

// ── View 2: Calibration view ──────────────────────────────────────────────────

interface CalibrationBand {
  confidence_band: string;
  total: number;
  autofixed: number;
  human_approved: number;
  dismissed: number;
  autofix_rate: number;
  human_approval_rate: number;
}
interface CalibrationResponse {
  bands: CalibrationBand[];
}
const FIXTURE_VIEW_2: CalibrationResponse = {
  bands: [
    { confidence_band: "0.50–0.60", total: 42, autofixed: 13, human_approved: 22, dismissed: 7,  autofix_rate: 0.31, human_approval_rate: 0.52 },
    { confidence_band: "0.60–0.70", total: 78, autofixed: 45, human_approved: 55, dismissed: 11, autofix_rate: 0.58, human_approval_rate: 0.71 },
    { confidence_band: "0.70–0.80", total: 124, autofixed: 92, human_approved: 102, dismissed: 9, autofix_rate: 0.74, human_approval_rate: 0.82 },
    { confidence_band: "0.80–0.90", total: 89, autofixed: 77, human_approved: 78, dismissed: 5,  autofix_rate: 0.87, human_approval_rate: 0.88 },
    { confidence_band: "0.90–0.95", total: 63, autofixed: 57, human_approved: 59, dismissed: 2,  autofix_rate: 0.91, human_approval_rate: 0.94 },
    { confidence_band: "0.95–1.00", total: 47, autofixed: 44, human_approved: 45, dismissed: 1,  autofix_rate: 0.94, human_approval_rate: 0.96 },
  ],
};

// ── View 3: Error attribution ─────────────────────────────────────────────────

interface ErrorAttributionRow {
  label_source: string;
  total_flagged: number;
  pct_of_total: number;
}
interface ErrorAttributionResponse {
  rows: ErrorAttributionRow[];
  total_flagged: number;
}
const FIXTURE_VIEW_3: ErrorAttributionResponse = {
  total_flagged: 462,
  rows: [
    { label_source: "diarization",          total_flagged: 156, pct_of_total: 0.34 },
    { label_source: "confidence_threshold", total_flagged: 103, pct_of_total: 0.22 },
    { label_source: "name_search",          total_flagged: 89,  pct_of_total: 0.19 },
    { label_source: "alias_lookup",         total_flagged: 67,  pct_of_total: 0.15 },
    { label_source: "manual",               total_flagged: 47,  pct_of_total: 0.10 },
  ],
};

// ── View 4: Writeback growth tracker ─────────────────────────────────────────
// Shape defined here becomes the contract Percy conforms to for /admin/feedback-loop/growth

interface WritebackGrowthResponse {
  learned_hosts: number;
  speaker_name_aliases: number;
  confirmation_count: number;
  learned_hosts_30d: number;
  speaker_name_aliases_30d: number;
  confirmation_count_30d: number;
}
const FIXTURE_VIEW_4: WritebackGrowthResponse = {
  learned_hosts: 234,
  speaker_name_aliases: 891,
  confirmation_count: 1247,
  learned_hosts_30d: 18,
  speaker_name_aliases_30d: 67,
  confirmation_count_30d: 143,
};

// ── Runtime shape guards ──────────────────────────────────────────────────────
// The admin endpoints behind this view have served shapes that diverge from the
// TS interfaces above twice (2026-06-12 and 2026-07-31 — same crash line both
// times). Interfaces are compile-time claims only: every fetch below coerces
// its JSON through these guards before it reaches state, so a drifted or empty
// endpoint renders its "no data" state instead of crashing to the ErrorBoundary.
function asValidArray<T>(value: unknown, isValid: (x: Partial<T>) => x is T): T[] {
  return Array.isArray(value) ? (value as Partial<T>[]).filter(isValid) : [];
}
function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

// ── Helper: MetricCard ────────────────────────────────────────────────────────

function MetricCard({
  title,
  value,
  subtitle,
  status,
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  status?: "green" | "amber" | "red";
}) {
  const borderColor =
    status === "green"
      ? "border-green-500"
      : status === "amber"
      ? "border-amber-500"
      : status === "red"
      ? "border-red-500"
      : "border-gray-200";

  return (
    <div
      className={`rounded-lg border bg-white p-4 ${
        status ? `border-l-4 ${borderColor}` : "border-gray-200"
      }`}
    >
      <h3 className="text-sm font-medium text-gray-500">{title}</h3>
      <p className="mt-2 text-2xl font-semibold text-gray-900">{value}</p>
      {subtitle && <p className="mt-1 text-xs text-gray-500">{subtitle}</p>}
    </div>
  );
}

// ── Shared section loading / error states ─────────────────────────────────────

function SectionSpinner() {
  return (
    <div className="flex h-32 items-center justify-center">
      <div className="h-6 w-6 animate-spin rounded-full border-4 border-gray-200 border-t-blue-600" />
    </div>
  );
}

function SectionError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
      {message}
      <button onClick={onRetry} className="ml-3 font-medium underline hover:text-red-800">
        Retry
      </button>
    </div>
  );
}

// ── Section 1: Compounding tracker ────────────────────────────────────────────

function CompoundingTrackerSection() {
  const [data, setData] = useState<CompoundingResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (STUB_MODE_VIEW_1) {
        setData(FIXTURE_VIEW_1);
        return;
      }
      const res = await fetch(`${API_BASE}/admin/speaker-audit/compounding?days=${days}`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Partial<CompoundingResponse>;
      setData({
        days: asNumber(json.days, days),
        buckets: asValidArray<CompoundingBucket>(
          json.buckets,
          (b): b is CompoundingBucket =>
            typeof b.period_start === "string" &&
            typeof b.flag_rate === "number" &&
            typeof b.autofix_rate === "number" &&
            typeof b.total_audits === "number",
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load compounding data");
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const buckets = data?.buckets ?? [];
  const maxFlagRate    = Math.max(...buckets.map((b) => b.flag_rate),    0.01);
  const maxAutofixRate = Math.max(...buckets.map((b) => b.autofix_rate), 0.01);

  return (
    <section>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-800">Compounding tracker</h2>
        <div className="flex gap-1">
          {[30, 60, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`rounded-md px-3 py-1 text-sm font-medium transition ${
                days === d
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200"
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>
      {STUB_MODE_VIEW_1 && (
        <p className="mb-2 text-xs text-amber-600">
          ⚠️ Stub data — /admin/speaker-audit/compounding not yet live
        </p>
      )}
      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <SectionError message={error} onRetry={fetchData} />
      ) : !data || buckets.length === 0 ? (
        <p className="text-sm text-gray-500">No data for this window.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-2 text-xs font-medium text-gray-500">Flag rate per period</h3>
            <div className="flex h-32 items-end gap-2">
              {buckets.map((b) => (
                <div key={b.period_start} className="flex flex-1 flex-col items-center">
                  <div
                    className="w-full rounded-t bg-red-300 transition-colors hover:bg-red-400"
                    style={{ height: `${Math.max((b.flag_rate / maxFlagRate) * 100, 2)}%` }}
                    title={`${(b.flag_rate * 100).toFixed(1)}% (${b.total_audits} audits)`}
                  />
                  <p className="mt-1 w-full truncate text-center text-xs text-gray-500">
                    {b.period_start.slice(0, 7)}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-white p-4">
            <h3 className="mb-2 text-xs font-medium text-gray-500">Autofix rate per period</h3>
            <div className="flex h-32 items-end gap-2">
              {buckets.map((b) => (
                <div key={b.period_start} className="flex flex-1 flex-col items-center">
                  <div
                    className="w-full rounded-t bg-green-300 transition-colors hover:bg-green-400"
                    style={{ height: `${Math.max((b.autofix_rate / maxAutofixRate) * 100, 2)}%` }}
                    title={`${(b.autofix_rate * 100).toFixed(1)}% autofix rate`}
                  />
                  <p className="mt-1 w-full truncate text-center text-xs text-gray-500">
                    {b.period_start.slice(0, 7)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

// ── Section 2: Calibration view ───────────────────────────────────────────────

function CalibrationSection() {
  const [data, setData] = useState<CalibrationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (STUB_MODE_VIEW_2) {
        setData(FIXTURE_VIEW_2);
        return;
      }
      const res = await fetch(`${API_BASE}/admin/speaker-audit/calibration`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Partial<CalibrationResponse>;
      setData({
        bands: asValidArray<CalibrationBand>(
          json.bands,
          (b): b is CalibrationBand =>
            typeof b.confidence_band === "string" &&
            typeof b.total === "number" &&
            typeof b.autofixed === "number" &&
            typeof b.human_approved === "number" &&
            typeof b.dismissed === "number" &&
            typeof b.autofix_rate === "number" &&
            typeof b.human_approval_rate === "number",
        ),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load calibration data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const bands = data?.bands ?? [];

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold text-gray-800">Calibration view</h2>
      {STUB_MODE_VIEW_2 && (
        <p className="mb-2 text-xs text-amber-600">
          ⚠️ Stub data — feat/vera-feedback-loop not yet on origin/main
        </p>
      )}
      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <SectionError message={error} onRetry={fetchData} />
      ) : !data || bands.length === 0 ? (
        <p className="text-sm text-gray-500">No calibration data.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Confidence band</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Total</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Autofixed</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Human approved</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Dismissed</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Autofix rate</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Approval rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {bands.map((band) => {
                const autofixColor =
                  band.autofix_rate >= 0.85
                    ? "text-green-600"
                    : band.autofix_rate < 0.6
                    ? "text-red-600"
                    : "text-amber-600";
                return (
                  <tr key={band.confidence_band}>
                    <td className="px-3 py-2 font-mono text-gray-700">{band.confidence_band}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{band.total}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{band.autofixed}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{band.human_approved}</td>
                    <td className="px-3 py-2 text-right text-gray-600">{band.dismissed}</td>
                    <td className={`px-3 py-2 text-right font-medium ${autofixColor}`}>
                      {(band.autofix_rate * 100).toFixed(0)}%
                    </td>
                    <td className="px-3 py-2 text-right font-medium text-gray-700">
                      {(band.human_approval_rate * 100).toFixed(0)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

// ── Section 3: Error attribution ──────────────────────────────────────────────

function ErrorAttributionSection() {
  const [data, setData] = useState<ErrorAttributionResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (STUB_MODE_VIEW_3) {
        setData(FIXTURE_VIEW_3);
        return;
      }
      const res = await fetch(`${API_BASE}/admin/speaker-audit/error-attribution`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Partial<ErrorAttributionResponse>;
      setData({
        rows: asValidArray<ErrorAttributionRow>(
          json.rows,
          (r): r is ErrorAttributionRow =>
            typeof r.label_source === "string" &&
            typeof r.total_flagged === "number" &&
            typeof r.pct_of_total === "number",
        ),
        total_flagged: asNumber(json.total_flagged),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load error attribution data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const rows = data?.rows ?? [];
  const maxFlagged = Math.max(...rows.map((r) => r.total_flagged), 1);

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold text-gray-800">Error attribution</h2>
      {STUB_MODE_VIEW_3 && (
        <p className="mb-2 text-xs text-amber-600">
          ⚠️ Stub data — feat/vera-feedback-loop not yet on origin/main
        </p>
      )}
      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <SectionError message={error} onRetry={fetchData} />
      ) : !data || rows.length === 0 ? (
        <p className="text-sm text-gray-500">No error attribution data.</p>
      ) : (
        <div className="space-y-3 rounded-lg border border-gray-200 bg-white p-4">
          {rows.map((row) => (
            <div key={row.label_source} className="flex items-center gap-3">
              <div className="w-40 shrink-0 truncate font-mono text-sm text-gray-700">
                {row.label_source}
              </div>
              <div className="h-5 flex-1 overflow-hidden rounded bg-gray-100">
                <div
                  className="h-full rounded bg-blue-400"
                  style={{ width: `${(row.total_flagged / maxFlagged) * 100}%` }}
                />
              </div>
              <div className="w-24 shrink-0 text-right text-sm text-gray-600">
                {row.total_flagged}{" "}
                <span className="text-gray-400">({(row.pct_of_total * 100).toFixed(0)}%)</span>
              </div>
            </div>
          ))}
          <p className="pt-1 text-xs text-gray-400">Total flagged: {data.total_flagged}</p>
        </div>
      )}
    </section>
  );
}

// ── Section 4: Writeback growth tracker ──────────────────────────────────────

function WritebackGrowthSection() {
  const [data, setData] = useState<WritebackGrowthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      if (STUB_MODE_VIEW_4) {
        setData(FIXTURE_VIEW_4);
        return;
      }
      const res = await fetch(`${API_BASE}/admin/feedback-loop/growth`, {
        headers: { "X-Admin-Key": ADMIN_KEY },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as Partial<WritebackGrowthResponse>;
      setData({
        learned_hosts: asNumber(json.learned_hosts),
        speaker_name_aliases: asNumber(json.speaker_name_aliases),
        confirmation_count: asNumber(json.confirmation_count),
        learned_hosts_30d: asNumber(json.learned_hosts_30d),
        speaker_name_aliases_30d: asNumber(json.speaker_name_aliases_30d),
        confirmation_count_30d: asNumber(json.confirmation_count_30d),
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load writeback growth data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return (
    <section>
      <h2 className="mb-3 text-base font-semibold text-gray-800">Writeback growth tracker</h2>
      {STUB_MODE_VIEW_4 && (
        <p className="mb-2 text-xs text-amber-600">
          ⚠️ Stub data — /admin/feedback-loop/growth pending Percy follow-up
        </p>
      )}
      {loading ? (
        <SectionSpinner />
      ) : error ? (
        <SectionError message={error} onRetry={fetchData} />
      ) : !data ? (
        <p className="text-sm text-gray-500">No writeback data.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <MetricCard
            title="learned_hosts"
            value={data.learned_hosts.toLocaleString()}
            subtitle={`+${data.learned_hosts_30d} in last 30d`}
            status={data.learned_hosts_30d > 0 ? "green" : "amber"}
          />
          <MetricCard
            title="speaker_name_aliases"
            value={data.speaker_name_aliases.toLocaleString()}
            subtitle={`+${data.speaker_name_aliases_30d} in last 30d`}
            status={data.speaker_name_aliases_30d > 0 ? "green" : "amber"}
          />
          <MetricCard
            title="confirmation_count"
            value={data.confirmation_count.toLocaleString()}
            subtitle={`+${data.confirmation_count_30d} in last 30d`}
            status={data.confirmation_count_30d > 0 ? "green" : "amber"}
          />
        </div>
      )}
    </section>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function AdminVeraPerformanceView() {
  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold text-gray-900">Vera Performance</h1>
        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
          v1 — stub data
        </span>
      </div>
      <CompoundingTrackerSection />
      <CalibrationSection />
      <ErrorAttributionSection />
      <WritebackGrowthSection />
    </div>
  );
}
